import { useQuery } from '@tanstack/react-query';
import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { SkladDayGridDay } from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/PageStates';
import { balanceColor } from '../../lib/balanceColors';
import { formatKopeks } from '../../lib/money';
import { useStickyTable } from '../../context/StickyTableContext';
import { DayDetailDrawer } from './DayDetailDrawer';

function money(n: number): JSX.Element {
  return n ? (
    <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      {formatKopeks(n)}
    </span>
  ) : (
    <Typography.Text type="secondary">—</Typography.Text>
  );
}

interface GridCol {
  key: string;
  title: string;
  render: (row: SkladDayGridDay) => JSX.Element;
  total: JSX.Element;
  fixed?: 'left';
  width?: number;
}

/**
 * Sklad «Jadval» view: one row per day; goods taken from this sklad spread
 * across a column per destination store (PURCHASE buy amount), plus the day's
 * payment to the sklad and the running debt.
 */
export function SkladDayGridTable({ id, month }: { id: string; month: string }): JSX.Element {
  const { t } = useTranslation();
  const sticky = useStickyTable();
  const [detailDate, setDetailDate] = useState<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.dayGrid('sklad', id, month),
    queryFn: () => reportsApi.skladDayGrid(id, month),
  });

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError || !query.data) return <ErrorBlock onRetry={() => void query.refetch()} />;
  const grid = query.data;
  const { totals } = grid;
  const closing = grid.days.at(-1)?.debtAfter ?? 0;

  const cols: GridCol[] = [
    {
      key: 'date',
      title: t('common.date'),
      fixed: 'left',
      width: 62,
      render: (row) => {
        const d = dayjs(row.date);
        const weekend = d.day() === 0 || d.day() === 6;
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums', color: weekend ? '#cf1322' : undefined }}>
            {d.format('DD.MM')}
          </span>
        );
      },
      total: <strong>{t('daygrid.total')}</strong>,
    },
    ...grid.stores.map<GridCol>((s) => ({
      key: `store-${s.id}`,
      title: s.name,
      render: (row) => money(row.purchasedByStore[s.id] ?? 0),
      total: money(totals.purchasedByStore[s.id] ?? 0),
    })),
    ...(grid.hasOther
      ? [
          {
            key: 'other',
            title: t('daygrid.other'),
            render: (row: SkladDayGridDay) => money(row.purchasedOther),
            total: money(totals.purchasedOther),
          },
        ]
      : []),
    {
      key: 'payment',
      title: t('daygrid.colPayment'),
      render: (row) => money(row.payment),
      total: money(totals.payment),
    },
    {
      key: 'debt',
      title: t('daygrid.colDebt'),
      render: (row) => (
        <span style={{ color: balanceColor('sklad', row.debtAfter), whiteSpace: 'nowrap' }}>
          {formatKopeks(row.debtAfter)}
        </span>
      ),
      total: (
        <span style={{ color: balanceColor('sklad', closing), whiteSpace: 'nowrap' }}>
          {formatKopeks(closing)}
        </span>
      ),
    },
  ];

  const columns: ColumnsType<SkladDayGridDay> = cols.map((c) => ({
    title: c.title,
    key: c.key,
    align: c.key === 'date' ? 'left' : 'right',
    fixed: c.fixed,
    width: c.width,
    render: (_v, row) => c.render(row),
  }));

  return (
    <>
      <Table<SkladDayGridDay>
        dataSource={grid.days}
        columns={columns}
        rowKey="date"
        size="small"
        bordered
        pagination={false}
        sticky={sticky}
        scroll={{ x: 'max-content' }}
        onRow={(row) => ({
          onClick: () => setDetailDate(row.date),
          style: { cursor: 'pointer' },
        })}
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row style={{ fontWeight: 600, background: 'rgba(0,0,0,0.02)' }}>
              {cols.map((c, i) => (
                <Table.Summary.Cell
                  key={c.key}
                  index={i}
                  align={c.key === 'date' ? 'left' : 'right'}
                >
                  {c.total}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <DayDetailDrawer kind="sklad" id={id} date={detailDate} onClose={() => setDetailDate(null)} />
    </>
  );
}
